/**
 * AvatarUploadDialog Component
 * Modal dialog for uploading a new agent avatar (image or video).
 * Supports drag-and-drop or file picker. Validates type and size before submit.
 */

import React, { useCallback, useEffect, useRef, useState } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  FormControlLabel,
  Switch,
  Chip,
  Typography,
  Box,
  CircularProgress,
  IconButton,
} from '@mui/material';
import { Upload, X, FileVideo, Image as ImageIcon, AlertCircle } from 'lucide-react';
import type { UploadAvatarPayload, AvatarCategory } from '../types';
import { formatFileSize } from '../types';

// ============================================
// Constants
// ============================================

const ALLOWED_IMAGE = ['image/jpeg', 'image/png', 'image/gif', 'image/webp', 'image/svg+xml'];
const ALLOWED_VIDEO = ['video/mp4', 'video/webm', 'video/quicktime'];
const ALLOWED_ALL = [...ALLOWED_IMAGE, ...ALLOWED_VIDEO];
const MAX_SIZE = 10 * 1024 * 1024; // 10 MB

const ACCEPT_STRING = ALLOWED_ALL.join(',');

// ============================================
// Props
// ============================================

interface AvatarUploadDialogProps {
  open: boolean;
  onClose: () => void;
  onSubmit: (payload: UploadAvatarPayload) => Promise<void>;
  isLoading: boolean;
}

// ============================================
// Tag input helper
// ============================================

interface TagInputProps {
  tags: string[];
  onAdd: (tag: string) => void;
  onRemove: (tag: string) => void;
}

const TagInput: React.FC<TagInputProps> = ({ tags, onAdd, onRemove }) => {
  const [inputValue, setInputValue] = useState('');

  const handleKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if ((e.key === 'Enter' || e.key === ',') && inputValue.trim()) {
      e.preventDefault();
      const tag = inputValue.trim().replace(',', '');
      if (tag && !tags.includes(tag)) {
        onAdd(tag);
      }
      setInputValue('');
    }
    if (e.key === 'Backspace' && !inputValue && tags.length > 0) {
      onRemove(tags[tags.length - 1]);
    }
  };

  return (
    <Box
      className="min-h-[48px] rounded border px-3 py-2"
      sx={{
        borderColor: (theme) =>
          theme.palette.mode === 'dark' ? 'rgba(255,255,255,0.23)' : 'rgba(0,0,0,0.23)',
        '&:hover': {
          borderColor: (theme) =>
            theme.palette.mode === 'dark' ? 'rgba(255,255,255,0.87)' : 'rgba(0,0,0,0.87)',
        },
        display: 'flex',
        flexWrap: 'wrap',
        gap: 0.5,
        alignItems: 'center',
      }}
    >
      {tags.map((tag) => (
        <Chip
          key={tag}
          label={tag}
          size="small"
          onDelete={() => onRemove(tag)}
          sx={{ height: 22, fontSize: '0.7rem' }}
        />
      ))}
      <input
        value={inputValue}
        onChange={(e) => setInputValue(e.target.value)}
        onKeyDown={handleKeyDown}
        placeholder={tags.length === 0 ? 'Type tag + Enter' : ''}
        className="min-w-[80px] flex-1 border-none bg-transparent text-sm outline-none dark:text-slate-200"
        style={{ border: 'none', outline: 'none', background: 'transparent' }}
      />
    </Box>
  );
};

// ============================================
// Main Component
// ============================================

export const AvatarUploadDialog: React.FC<AvatarUploadDialogProps> = ({
  open,
  onClose,
  onSubmit,
  isLoading,
}) => {
  // File state
  const [file, setFile] = useState<File | null>(null);
  const [fileError, setFileError] = useState('');
  const [isDragging, setIsDragging] = useState(false);
  const [preview, setPreview] = useState<string>('');

  // Form state
  const [name, setName] = useState('');
  const [category, setCategory] = useState<AvatarCategory | ''>('');
  const [tags, setTags] = useState<string[]>([]);
  const [isDefault, setIsDefault] = useState(false);
  const [nameError, setNameError] = useState('');

  const fileInputRef = useRef<HTMLInputElement>(null);

  // ---- Reset all state whenever the dialog is closed (covers success, cancel, X) ----

  useEffect(() => {
    if (!open) {
      setFile(null);
      setFileError('');
      setIsDragging(false);
      setPreview((prev) => {
        if (prev) URL.revokeObjectURL(prev);
        return '';
      });
      setName('');
      setCategory('');
      setTags([]);
      setIsDefault(false);
      setNameError('');
      if (fileInputRef.current) fileInputRef.current.value = '';
    }
  }, [open]);

  // ---- File helpers ----

  const validateFile = (f: File): string => {
    if (!ALLOWED_ALL.includes(f.type)) {
      return `Unsupported type: ${f.type}. Accepted: JPEG, PNG, GIF, WebP, SVG, MP4, WebM, MOV`;
    }
    if (f.size > MAX_SIZE) {
      return `File too large: ${formatFileSize(f.size)}. Maximum is 10 MB`;
    }
    return '';
  };

  const applyFile = (f: File) => {
    const err = validateFile(f);
    if (err) {
      setFileError(err);
      return;
    }
    setFileError('');
    setFile(f);
    // Auto-fill name from filename (without extension)
    if (!name) {
      setName(f.name.replace(/\.[^.]+$/, '').replace(/[_-]/g, ' '));
    }
    // Create preview URL
    const url = URL.createObjectURL(f);
    setPreview(url);
  };

  const clearFile = () => {
    setFile(null);
    setFileError('');
    if (preview) URL.revokeObjectURL(preview);
    setPreview('');
    if (fileInputRef.current) fileInputRef.current.value = '';
  };

  // ---- Drag & drop ----

  const handleDrop = useCallback((e: React.DragEvent) => {
    e.preventDefault();
    setIsDragging(false);
    const dropped = e.dataTransfer.files[0];
    if (dropped) applyFile(dropped);
  }, [name]); // eslint-disable-line react-hooks/exhaustive-deps

  const handleDragOver = (e: React.DragEvent) => {
    e.preventDefault();
    setIsDragging(true);
  };

  const handleDragLeave = () => setIsDragging(false);

  // ---- Submit ----

  const handleSubmit = async () => {
    let valid = true;

    if (!file) {
      setFileError('Please select a file to upload');
      valid = false;
    }
    if (!name.trim()) {
      setNameError('Avatar name is required');
      valid = false;
    }
    if (!valid) return;

    await onSubmit({
      file: file!,
      name: name.trim(),
      category: category || undefined,
      tags: tags.length > 0 ? JSON.stringify(tags) : undefined,
      isDefault,
    });
  };

  // ---- Close (reset is handled by the useEffect above) ----

  const handleClose = () => {
    if (isLoading) return;
    onClose();
  };

  const isImage = file ? ALLOWED_IMAGE.includes(file.type) : false;
  const isVideo = file ? ALLOWED_VIDEO.includes(file.type) : false;

  return (
    <Dialog
      open={open}
      onClose={handleClose}
      maxWidth="sm"
      fullWidth
      PaperProps={{
        sx: {
          bgcolor: (theme) => (theme.palette.mode === 'dark' ? '#1E293B' : '#ffffff'),
          backgroundImage: 'none',
        },
      }}
    >
      <DialogTitle className="flex items-center justify-between text-gray-900 dark:text-slate-100">
        <span className="text-lg font-semibold">Upload Agent Avatar</span>
        <IconButton size="small" onClick={handleClose} disabled={isLoading}>
          <X size={18} />
        </IconButton>
      </DialogTitle>

      <DialogContent dividers>
        <Box className="flex flex-col gap-4 pt-1">

          {/* ---- Drop zone / preview ---- */}
          {!file ? (
            <Box
              onDrop={handleDrop}
              onDragOver={handleDragOver}
              onDragLeave={handleDragLeave}
              onClick={() => fileInputRef.current?.click()}
              className="flex cursor-pointer flex-col items-center justify-center rounded-xl border-2 border-dashed p-8 text-center transition-colors"
              sx={{
                borderColor: isDragging
                  ? '#3B82F6'
                  : fileError
                  ? '#EF4444'
                  : (theme) =>
                      theme.palette.mode === 'dark'
                        ? 'rgba(255,255,255,0.15)'
                        : 'rgba(0,0,0,0.15)',
                bgcolor: isDragging
                  ? 'rgba(59,130,246,0.05)'
                  : (theme) =>
                      theme.palette.mode === 'dark' ? 'rgba(255,255,255,0.03)' : '#F8FAFC',
                '&:hover': { borderColor: '#3B82F6', bgcolor: 'rgba(59,130,246,0.05)' },
              }}
            >
              <Upload size={32} className="mb-3 text-blue-400" />
              <Typography
                variant="body2"
                className="mb-1 font-medium text-gray-700 dark:text-slate-300"
              >
                Drag & drop or click to select
              </Typography>
              <Typography variant="caption" className="text-gray-400 dark:text-slate-500">
                Images: JPEG, PNG, GIF, WebP, SVG
              </Typography>
              <Typography variant="caption" className="text-gray-400 dark:text-slate-500">
                Videos: MP4, WebM, MOV · Max 10 MB
              </Typography>
              <input
                ref={fileInputRef}
                type="file"
                accept={ACCEPT_STRING}
                className="hidden"
                onChange={(e) => {
                  const f = e.target.files?.[0];
                  if (f) applyFile(f);
                }}
              />
            </Box>
          ) : (
            /* ---- Preview ---- */
            <Box className="relative overflow-hidden rounded-xl">
              {isImage && (
                <img
                  src={preview}
                  alt="Preview"
                  className="max-h-48 w-full object-contain"
                  style={{ background: '#0f172a' }}
                />
              )}
              {isVideo && (
                <video
                  src={preview}
                  controls
                  className="max-h-48 w-full"
                  style={{ background: '#0f172a' }}
                />
              )}
              {/* File info bar */}
              <Box
                className="flex items-center justify-between rounded-b-xl px-3 py-2"
                sx={{
                  bgcolor: (theme) =>
                    theme.palette.mode === 'dark' ? 'rgba(0,0,0,0.4)' : 'rgba(0,0,0,0.06)',
                }}
              >
                <Box className="flex items-center gap-2">
                  {isVideo ? (
                    <FileVideo size={16} className="text-blue-400" />
                  ) : (
                    <ImageIcon size={16} className="text-blue-400" />
                  )}
                  <Typography
                    variant="caption"
                    className="max-w-[180px] truncate text-gray-600 dark:text-slate-400"
                  >
                    {file.name}
                  </Typography>
                  <Typography variant="caption" className="text-gray-400 dark:text-slate-500">
                    {formatFileSize(file.size)}
                  </Typography>
                </Box>
                <IconButton size="small" onClick={clearFile} disabled={isLoading}>
                  <X size={14} />
                </IconButton>
              </Box>
            </Box>
          )}

          {/* File error */}
          {fileError && (
            <Box className="flex items-center gap-2 rounded-lg bg-red-50 px-3 py-2 dark:bg-red-900/20">
              <AlertCircle size={16} className="text-red-500" />
              <Typography variant="caption" className="text-red-600 dark:text-red-400">
                {fileError}
              </Typography>
            </Box>
          )}

          {/* ---- Name ---- */}
          <TextField
            label="Avatar Name *"
            fullWidth
            size="small"
            value={name}
            onChange={(e) => {
              setName(e.target.value);
              if (e.target.value.trim()) setNameError('');
            }}
            error={Boolean(nameError)}
            helperText={nameError}
            InputProps={{
              sx: {
                color: (theme) => (theme.palette.mode === 'dark' ? '#E2E8F0' : undefined),
              },
            }}
            InputLabelProps={{
              sx: {
                color: (theme) => (theme.palette.mode === 'dark' ? '#94A3B8' : undefined),
              },
            }}
          />

          {/* ---- Category ---- */}
          <FormControl fullWidth size="small">
            <InputLabel
              sx={{ color: (theme) => (theme.palette.mode === 'dark' ? '#94A3B8' : undefined) }}
            >
              Category
            </InputLabel>
            <Select
              value={category}
              label="Category"
              onChange={(e) => setCategory(e.target.value as AvatarCategory | '')}
              sx={{
                color: (theme) => (theme.palette.mode === 'dark' ? '#E2E8F0' : undefined),
              }}
            >
              <MenuItem value="">None</MenuItem>
              <MenuItem value="robot">Robot</MenuItem>
              <MenuItem value="anime">Anime</MenuItem>
              <MenuItem value="human">Human</MenuItem>
              <MenuItem value="abstract">Abstract</MenuItem>
            </Select>
          </FormControl>

          {/* ---- Tags ---- */}
          <Box>
            <Typography
              variant="caption"
              className="mb-1 block text-gray-500 dark:text-slate-400"
            >
              Tags (press Enter or comma to add)
            </Typography>
            <TagInput
              tags={tags}
              onAdd={(t) => setTags((prev) => [...prev, t])}
              onRemove={(t) => setTags((prev) => prev.filter((x) => x !== t))}
            />
          </Box>

          {/* ---- Is Default ---- */}
          <FormControlLabel
            control={
              <Switch
                checked={isDefault}
                onChange={(e) => setIsDefault(e.target.checked)}
                sx={{
                  '& .MuiSwitch-switchBase.Mui-checked': { color: '#3B82F6' },
                  '& .MuiSwitch-switchBase.Mui-checked + .MuiSwitch-track': { bgcolor: '#3B82F6' },
                }}
              />
            }
            label={
              <Typography variant="body2" className="text-gray-700 dark:text-slate-300">
                Set as default avatar
              </Typography>
            }
          />
        </Box>
      </DialogContent>

      <DialogActions sx={{ px: 3, py: 2 }}>
        <Button
          onClick={handleClose}
          disabled={isLoading}
          variant="outlined"
          sx={{ textTransform: 'none', borderColor: 'rgba(107,114,128,0.4)', color: '#6B7280' }}
        >
          Cancel
        </Button>
        <Button
          onClick={handleSubmit}
          disabled={isLoading}
          variant="contained"
          startIcon={isLoading ? <CircularProgress size={16} color="inherit" /> : <Upload size={16} />}
          sx={{
            textTransform: 'none',
            bgcolor: '#3B82F6',
            '&:hover': { bgcolor: '#2563EB' },
            fontWeight: 600,
          }}
        >
          {isLoading ? 'Uploading…' : 'Upload Avatar'}
        </Button>
      </DialogActions>
    </Dialog>
  );
};
